import React, { useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import {
  layerDefGroupingSelector,
  layerDefGroupVisibilitySelector,
} from "../../layers/recoil";

import { Box, Checkbox, Typography, IconButton, Collapse, Tooltip } from "@mui/material";

import CircleIcon from "@mui/icons-material/Circle";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";
import ContrastIcon from "@mui/icons-material/Contrast";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";

import LayerCheckbox from "./LayerCheckbox";
import LayerInfo from "./LayerInfo.jsx";

import { layerGroupCanBeVisible, layerGroupRequiresLogin } from "../../layers/recoil";
import { isLoggedInSelector } from "../../login/loginAtoms.js";
import { groupingInfo, groupingLabels } from "../../layers/layerDefs";

export default function LayerGroupCheckboxList({ layerIDs }) {
  const [collapseOpen, setCollapseOpen] = useState(false);

  const grouping = useRecoilValue(layerDefGroupingSelector(layerIDs[0]));

  const groupLabel = groupingLabels[grouping];

  const info = groupingInfo.hasOwnProperty(grouping)
    ? groupingInfo[grouping]
    : null;

  const [groupVisibility, setGroupVisibility] = useRecoilState(
    layerDefGroupVisibilitySelector(grouping)
  );

  const isLoggedIn = useRecoilValue(isLoggedInSelector)
  const groupRequiresLogin = useRecoilValue(
    layerGroupRequiresLogin(layerIDs)
  );
  const someLayersCanBeVisible = useRecoilValue(
    layerGroupCanBeVisible(layerIDs)
  );
  
  const noLayersCanBeVisible = !someLayersCanBeVisible
  const authorisedToViewLayers = !groupRequiresLogin || isLoggedIn
  const groupDisabled = noLayersCanBeVisible || !authorisedToViewLayers

  const switchCollapseState = () => {
    setCollapseOpen((oldCollapseOpen) => !oldCollapseOpen);
  };

  const handleChange = (event) => {
    setGroupVisibility(event.target.checked);
  };

  const getTooltipTitle = () => {
    if (groupRequiresLogin && !isLoggedIn) {
      return "HSDS login required to view layers in this group"
    }

    if (noLayersCanBeVisible) {
      return "No layer data available for the selected high street"
    }
    
    return ""
  }

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexDirection: "row",
          justifyContent: "flex-start",
        }}
      >
        <Tooltip
          arrow
          placement="left"
          title={getTooltipTitle()}>
          <span>
          <Checkbox
            disabled={groupDisabled}
            sx={{ p: 1 }}
            icon={<CircleOutlinedIcon />}
            checkedIcon={<CircleIcon />}
            indeterminateIcon={<ContrastIcon />}
            indeterminate={false}
            checked={groupVisibility === "all" || groupVisibility === "some"}
            onChange={handleChange}
          />
          </span>
        </Tooltip>
        <Typography
          sx={{ cursor: "pointer", opacity: !groupDisabled ? 1 : 0.3 }}
          onClick={switchCollapseState}
        >
          {groupLabel}
        </Typography>
        {info && <LayerInfo {...info} />}

        <IconButton onClick={switchCollapseState}>
          {collapseOpen ? (
            <KeyboardDoubleArrowUpIcon />
          ) : (
            <KeyboardDoubleArrowDownIcon />
          )}
        </IconButton>
      </Box>

      <Collapse in={collapseOpen}>
        {layerIDs.map((layerID) => (
          <Box
            key={layerID}
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "flex-start",
              flexGrow: 1,
              pl: "30px",
              overflowY: "auto",
            }}
          >
            <LayerCheckbox layerID={layerID} />
          </Box>
        ))}
      </Collapse>
    </>
  );
}
